/**
 * Constants exposed to players as global variables.
 * UNIT_* values correspond to UnitType enum values.
 * BUILDING_* values correspond to BuildingType enum values.
 * For unit data (cost, life, etc.), see UNIT_DEFINITIONS.
 * For building data (cost, life, etc.), see BUILDING_DEFINITIONS.
 */
export const CONSTANTS = {
  BUILDING_HUB: 1, // BuildingType.Hub
  BUILDING_HOUSE: 2, // BuildingType.House
  BUILDING_CANNON: 3, // BuildingType.Cannon
  BUILDING_ORE_PROCESSOR: 4, // BuildingType.OreProcessor
  BUILDING_ORBITAL_MASS_DRIVER: 5, // BuildingType.OrbitalMassDriver
  BUILDING_ARTILLERY: 6, // BuildingType.Artillery
  BUILDING_LIGHT_FACTORY: 7, // BuildingType.LightFactory
  BUILDING_SOLAR_FARM: 8, // BuildingType.SolarFarm
  BUILDING_ENERGY_STORAGE: 9, // BuildingType.EnergyStorage
  BUILDING_ARTILLERY_FACTORY: 10, // BuildingType.ArtilleryFactory
  BUILDING_WALL: 11, // BuildingType.Wall
  BUILDING_LASER_CANNON: 12, // BuildingType.LaserCannon
  BUILDING_MISSILE_CANNON: 13, // BuildingType.MissileCannon
  UNIT_WORKER: 1, // UnitType.Worker
  UNIT_TANK: 2, // UnitType.Tank
  UNIT_ARCHER: 3, // UnitType.Archer
  UNIT_CRAWLER: 5, // UnitType.Crawler
  UNIT_LIGHT_TANK: 6, // UnitType.LightTank
  UNIT_MEDIUM_TANK: 7, // UnitType.MediumTank
  UNIT_HEAVY_TANK: 8, // UnitType.HeavyTank
  UNIT_ARTILLERY: 9, // UnitType.Artillery
  UNIT_SCOUT_PLANE: 10, // UnitType.ScoutPlane
  WEAPON_KINETIC_LIGHT_MACHINE_GUN: 1,
  WEAPON_EXPLOSIVE_MEDIUM_MISSILE_RACK: 2,
  WEAPON_KINETIC_LIGHT_SHOTGUN: 3,
  WEAPON_KINETIC_MEDIUM_AUTOCANNON: 4,
  WEAPON_EXPLOSIVE_HEAVY_PROJECTILE: 5,
  WEAPON_KINETIC_LIGHT_TANK: 6,
  WEAPON_KINETIC_MEDIUM_TANK: 7,
  WEAPON_KINETIC_HEAVY_TANK: 8,
  WEAPON_KINETIC_ARTILLERY: 9,
  WEAPON_ENERGY_LASER_BEAM: 10,
  WEAPON_EXPLOSIVE_MISSILE: 11,
  WEAPON_AIR_SCOUT_MACHINE_GUN: 12,
  STANCE_STAND_GROUND: 1,
  STANCE_DEFENSIVE: 2,
  STANCE_AGGRESSIVE: 3,
}

export const SHOW_ALL_ORE = true;

/**
 * Whether the unit will move to attack enemies or not
 */
export enum UnitStance {
  /**
   * Unit will only move when told to do so
   */
  StandGround = 1,

  /**
   * Unit will move to attack nearby enemies
   */
  Defensive = 2,

  /**
   * Unit will move to attack enemies, even if they are far away
   */
  Aggressive = 3
}

export enum UnitType {
  Worker = 1,
  Tank = 2,
  Archer = 3,
  Crawler = 5,
  LightTank = 6,
  MediumTank = 7,
  HeavyTank = 8,
  Artillery = 9,
  ScoutPlane = 10
}

export enum UnitNamedType {
  Worker = 'Worker',
  Tank = 'Tank',
  Archer = 'Archer',
  Crawler = 'Crawler',
  LightTank = 'LightTank',
  MediumTank = 'MediumTank',
  HeavyTank = 'HeavyTank',
  Artillery = 'Artillery',
  ScoutPlane = 'ScoutPlane',
}

/**
 * Complete unit definition containing all unit-related data
 */
export interface UnitDefinition {
  type: UnitType;
  name: UnitNamedType;
  cost: number;
  energyCost: number;
  life: number;
  sightRange: number;
  defaultWeapon: WeaponType | null;
}




export enum BuildingType {
  Hub = 1,
  House = 2,
  Cannon = 3,
  OreProcessor = 4,
  OrbitalMassDriver = 5,
  Artillery = 6,
  LightFactory = 7,
  SolarFarm = 8,
  EnergyStorage = 9,
  ArtilleryFactory = 10,
  Wall = 11,
  LaserCannon = 12,
  MissileCannon = 13,
}

export type BuildingTypeString = keyof typeof BUILDING_DEFINITIONS;

/**
 * Complete building definition containing all building-related data
 */
export interface BuildingDefinition {
  type: BuildingType;
  name: BuildingTypeString;
  cost: number;
  energyCost: number;
  life: number;
  buildSeparationDistance: number;
  sightRange: number;
  defaultWeapon: WeaponType | null;
  typeLimit: number; // Maximum number of this building type allowed
}

export interface PlayerUnit {
  id: number;
  k: UnitType;
  w?: WeaponType;
  x: number;
  y: number;
  s: UnitStance;
  tx: number;
  ty: number;
  life: number;
  gx?: number; // Guard position X (for Defensive stance)
  gy?: number; // Guard position Y (for Defensive stance)
}

export interface ExtendedPlayerUnit extends PlayerUnit {
  ore: number;
}

export interface WorldPlayerUnit extends PlayerUnit {
  playerId: number;
  tx: number;
  ty: number;
  life: number;
  ore?: number;
}

export interface PlayerBuilding {
  id: number;
  k: BuildingType;
  x: number;
  y: number;
  status?: string;
  life: number;
  lastSpawnTime?: number;
}

export interface ExtendedPlayerBuilding extends PlayerBuilding {
  ore: number;
}

export interface AccountData {
  id: number;
  username: string;
  hash: string;
  code: string;
}
export interface PlayerData {
  colour: string;
  units: PlayerUnit[];
  buildings: PlayerBuilding[];
  baseX: number | null;
  baseY: number | null;
  resources: {
    credits: number;
    ore: number;
    metal: number;
    energy: number;
    energyLimit: number;
    control: number;
    computeTime: number;
  }
  data: string;
}

export interface WorldPlayerData {
  [playerId: number]: PlayerData
}

export enum MessageType {
  PlayerConnected = "playerConnected",
  PlayerId = "playerId",
  ServerDisconnect = "serverDisconnect",
  PlayerRace = "playerRace",
  GetTerrain = "getTerrain",
  GotTerrain = "gotTerrain",
  GetOres = "getOres",
  GotOres = "gotOres",
  GetSync = "getSync",
  GotSync = "gotSync",
  SpawnWorker = "spawnWorker",
  SpawnTank = "spawnTank",
  SpawnUnit = "spawnUnit",
  SpawnBuilding = "spawnBuilding",
  Move = "move",
  BulkMove = "bulkMove",
  Attack = "attack",
  BulkAttack = "bulkAttack",
  SetUnitStance = "setUnitStance",
  Action = "action",
  DestroyUnit = "destroyUnit",
  DestroyBuilding = "destroyBuilding",
  GameUpdateDelta = "gameUpdateDelta"
}

export const messageTypes = [
  MessageType.SpawnWorker,
  MessageType.SpawnTank,
  MessageType.SpawnUnit,
  MessageType.SpawnBuilding,
  MessageType.Move,
  MessageType.BulkMove,
  MessageType.Attack,
  MessageType.BulkAttack,
  MessageType.SetUnitStance,
  MessageType.Action,
  MessageType.DestroyUnit,
  MessageType.DestroyBuilding
];

export enum WeaponType {
  KineticLightMachineGun = 1,
  ExplosiveMediumMissileRack = 2,
  KineticLightShotgun = 3,
  KineticMediumAutocannon = 4,
  ExplosiveHeavyProjectile = 5,
  KineticLightTank = 6,
  KineticMediumTank = 7,
  KineticHeavyTank = 8,
  KineticArtillery = 9,
  EnergyLaserBeam = 10,
  ExplosiveMissile = 11,
  AirScoutMachineGun = 12,
}

/**
 * Centralized dictionary containing all unit definitions.
 * This is the single source of truth for unit data.
 */
export const UNIT_DEFINITIONS: { [key in UnitType]: UnitDefinition } = {
  [UnitType.Worker]: {
    type: UnitType.Worker,
    name: UnitNamedType.Worker,
    cost: 10,
    energyCost: 5,
    life: 10,
    sightRange: 300,
    defaultWeapon: null,
  },
  [UnitType.Tank]: {
    type: UnitType.Tank,
    name: UnitNamedType.Tank,
    cost: 50,
    energyCost: 20,
    life: 100,
    sightRange: 500,
    defaultWeapon: WeaponType.KineticLightTank,
  },
  [UnitType.Archer]: {
    type: UnitType.Archer,
    name: UnitNamedType.Archer,
    cost: 80,
    energyCost: 30,
    life: 100,
    sightRange: 400,
    defaultWeapon: null,
  },
  [UnitType.Crawler]: {
    type: UnitType.Crawler,
    name: UnitNamedType.Crawler,
    cost: 20,
    energyCost: 10,
    life: 300,
    sightRange: 350,
    defaultWeapon: WeaponType.KineticLightMachineGun,
  },
  [UnitType.LightTank]: {
    type: UnitType.LightTank,
    name: UnitNamedType.LightTank,
    cost: 100,
    energyCost: 40,
    life: 120,
    sightRange: 500,
    defaultWeapon: WeaponType.KineticLightTank,
  },
  [UnitType.MediumTank]: {
    type: UnitType.MediumTank,
    name: UnitNamedType.MediumTank,
    cost: 200,
    energyCost: 60,
    life: 180,
    sightRange: 500,
    defaultWeapon: WeaponType.KineticMediumTank,
  },
  [UnitType.HeavyTank]: {
    type: UnitType.HeavyTank,
    name: UnitNamedType.HeavyTank,
    cost: 300,
    energyCost: 80,
    life: 250,
    sightRange: 500,
    defaultWeapon: WeaponType.KineticHeavyTank,
  },
  [UnitType.Artillery]: {
    type: UnitType.Artillery,
    name: UnitNamedType.Artillery,
    cost: 400,
    energyCost: 100,
    life: 100,
    sightRange: 500,
    defaultWeapon: WeaponType.KineticArtillery,
  },
  [UnitType.ScoutPlane]: {
    type: UnitType.ScoutPlane,
    name: UnitNamedType.ScoutPlane,
    cost: 25,
    energyCost: 15,
    life: 50,
    sightRange: 900,
    defaultWeapon: WeaponType.AirScoutMachineGun,
  },
};

/**
 * Convert unit named type to unit type using the definitions dictionary
 */
export const unitNamedTypeToUnitType = (unitNamedType: UnitNamedType): UnitType => {
  const entry = Object.values(UNIT_DEFINITIONS).find(def => def.name === unitNamedType);
  if (!entry) {
    throw new Error(`Unknown unit named type: ${unitNamedType}`);
  }
  return entry.type;
}


/**
 * Centralized dictionary containing all building definitions.
 * This is the single source of truth for building data.
 */
export const BUILDING_DEFINITIONS: { [key in BuildingType]: BuildingDefinition } = {
  [BuildingType.Hub]: {
    type: BuildingType.Hub,
    name: BuildingType.Hub,
    cost: 1000,
    energyCost: 0,
    life: 10000,
    buildSeparationDistance: 200,
    sightRange: 1000,
    defaultWeapon: null,
    typeLimit: 1,
  },
  [BuildingType.House]: {
    type: BuildingType.House,
    name: BuildingType.House,
    cost: 5,
    energyCost: 5,
    life: 1000,
    buildSeparationDistance: 50,
    sightRange: 400,
    defaultWeapon: null,
    typeLimit: 0,
  },
  [BuildingType.Cannon]: {
    type: BuildingType.Cannon,
    name: BuildingType.Cannon,
    cost: 20,
    energyCost: 15,
    life: 1000,
    buildSeparationDistance: 50,
    sightRange: 700,
    defaultWeapon: WeaponType.KineticMediumAutocannon,
    typeLimit: 4,
  },
  [BuildingType.OreProcessor]: {
    type: BuildingType.OreProcessor,
    name: BuildingType.OreProcessor,
    cost: 10,
    energyCost: 10,
    life: 200,
    buildSeparationDistance: 50,
    sightRange: 400,
    defaultWeapon: null,
    typeLimit: 3,
  },
  [BuildingType.OrbitalMassDriver]: {
    type: BuildingType.OrbitalMassDriver,
    name: BuildingType.OrbitalMassDriver,
    cost: 50,
    energyCost: 50,
    life: 500,
    buildSeparationDistance: 50,
    sightRange: 500,
    defaultWeapon: null,
    typeLimit: 1,
  },
  [BuildingType.Artillery]: {
    type: BuildingType.Artillery,
    name: BuildingType.Artillery,
    cost: 100,
    energyCost: 40,
    life: 1500,
    buildSeparationDistance: 50,
    sightRange: 800,
    defaultWeapon: WeaponType.ExplosiveHeavyProjectile,
    typeLimit: 2,
  },
  [BuildingType.LightFactory]: {
    type: BuildingType.LightFactory,
    name: BuildingType.LightFactory,
    cost: 75,
    energyCost: 30,
    life: 2500,
    buildSeparationDistance: 50,
    sightRange: 400,
    defaultWeapon: null,
    typeLimit: 2,
  },
  [BuildingType.SolarFarm]: {
    type: BuildingType.SolarFarm,
    name: BuildingType.SolarFarm,
    cost: 5,
    energyCost: 0,
    life: 200,
    buildSeparationDistance: 150,
    sightRange: 300,
    defaultWeapon: null,
    typeLimit: 10,
  },
  [BuildingType.EnergyStorage]: {
    type: BuildingType.EnergyStorage,
    name: BuildingType.EnergyStorage,
    cost: 10,
    energyCost: 5,
    life: 200,
    buildSeparationDistance: 50,
    sightRange: 300,
    defaultWeapon: null,
    typeLimit: 10,
  },
  [BuildingType.ArtilleryFactory]: {
    type: BuildingType.ArtilleryFactory,
    name: BuildingType.ArtilleryFactory,
    cost: 200,
    energyCost: 60,
    life: 1000,
    buildSeparationDistance: 50,
    sightRange: 400,
    defaultWeapon: null,
    typeLimit: 1,
  },
  [BuildingType.Wall]: {
    type: BuildingType.Wall,
    name: BuildingType.Wall,
    cost: 1,
    energyCost: 1,
    life: 100,
    buildSeparationDistance: 50,
    sightRange: 100,
    defaultWeapon: null,
    typeLimit: 0,
  },
  [BuildingType.LaserCannon]: {
    type: BuildingType.LaserCannon,
    name: BuildingType.LaserCannon,
    cost: 30,
    energyCost: 20,
    life: 1200,
    buildSeparationDistance: 50,
    sightRange: 750,
    defaultWeapon: WeaponType.EnergyLaserBeam,
    typeLimit: 4,
  },
  [BuildingType.MissileCannon]: {
    type: BuildingType.MissileCannon,
    name: BuildingType.MissileCannon,
    cost: 40,
    energyCost: 25,
    life: 1100,
    buildSeparationDistance: 50,
    sightRange: 900,
    defaultWeapon: WeaponType.ExplosiveMissile,
    typeLimit: 4,
  },
};

/**
 * Convert building type string to building type using the definitions dictionary
 */
export const buildingTypeStringToBuildingType = (buildingTypeString: BuildingTypeString): BuildingType => {
  const entry = Object.values(BUILDING_DEFINITIONS).find(def => def.name === buildingTypeString);
  if (!entry) {
    throw new Error(`Unknown building type string: ${buildingTypeString}`);
  }
  return entry.type;
}


export interface WeaponStats {
  damage: number,
  range: number,
  cooldown: number,
  shotCount: number,
}


export const WeaponStatsMap: { [key in WeaponType]: WeaponStats } = {
  [WeaponType.KineticLightMachineGun]: {
    damage: 1,
    range: 300,
    cooldown: 1,
    shotCount: 1,
  },
  [WeaponType.ExplosiveMediumMissileRack]: {
    damage: 5,
    range: 700,
    cooldown: 5,
    shotCount: 1,
  },
  [WeaponType.KineticLightShotgun]: {
    damage: 5,
    range: 250,
    cooldown: 2,
    shotCount: 1,
  },
  [WeaponType.KineticMediumAutocannon]: {
    damage: 20,
    range: 500,
    cooldown: 1,
    shotCount: 5,
  },
  [WeaponType.ExplosiveHeavyProjectile]: {
    damage: 100,
    range: 3000,
    cooldown: 30,
    shotCount: 1,
  },
  [WeaponType.KineticLightTank]: {
    damage: 5,
    range: 300,
    cooldown: 5,
    shotCount: 1,
  },
  [WeaponType.KineticMediumTank]: {
    damage: 10,
    range: 350,
    cooldown: 7,
    shotCount: 1,
  },
  [WeaponType.KineticHeavyTank]: {
    damage: 20,
    range: 400,
    cooldown: 8,
    shotCount: 1,
  },
  [WeaponType.KineticArtillery]: {
    damage: 50,
    range: 500,
    cooldown: 10,
    shotCount: 1,
  },
  [WeaponType.EnergyLaserBeam]: {
    damage: 15,
    range: 600,
    cooldown: 2,
    shotCount: 1,
  },
  [WeaponType.ExplosiveMissile]: {
    damage: 30,
    range: 800,
    cooldown: 5,
    shotCount: 1,
  },
  [WeaponType.AirScoutMachineGun]: {
    damage: 1,
    range: 350,
    cooldown: 1,
    shotCount: 1,
  }
};

export enum WeaponNamedType {
  KineticLightMachineGun = 'KineticLightMachineGun',
  ExplosiveMediumMissileRack = 'ExplosiveMediumMissileRack',
  KineticLightShotgun = 'KineticLightShotgun',
  KineticMediumAutocannon = 'KineticMediumAutocannon',
  ExplosiveHeavyProjectile = 'ExplosiveHeavyProjectile',
  KineticLightTank = 'KineticLightTank',
  KineticMediumTank = 'KineticMediumTank',
  KineticHeavyTank = 'KineticHeavyTank',
  KineticArtillery = 'KineticArtillery',
  EnergyLaserBeam = 'EnergyLaserBeam',
  ExplosiveMissile = 'ExplosiveMissile',
  AirScoutMachineGun = 'AirScoutMachineGun',
}

export const weaponNamedTypeToWeaponType = (weaponNamedType: WeaponNamedType): WeaponType => {
  let it = weaponNamedType as WeaponNamedType;
  switch (it) {
    case WeaponNamedType.KineticLightMachineGun:
      return WeaponType.KineticLightMachineGun;
    case WeaponNamedType.ExplosiveMediumMissileRack:
      return WeaponType.ExplosiveMediumMissileRack;
    case WeaponNamedType.KineticLightShotgun:
      return WeaponType.KineticLightShotgun;
    case WeaponNamedType.KineticMediumAutocannon:
      return WeaponType.KineticMediumAutocannon;
    case WeaponNamedType.ExplosiveHeavyProjectile:
      return WeaponType.ExplosiveHeavyProjectile;
    case WeaponNamedType.KineticLightTank:
      return WeaponType.KineticLightTank;
    case WeaponNamedType.KineticMediumTank:
      return WeaponType.KineticMediumTank;
    case WeaponNamedType.KineticHeavyTank:
      return WeaponType.KineticHeavyTank;
    case WeaponNamedType.KineticArtillery:
      return WeaponType.KineticArtillery;
    case WeaponNamedType.EnergyLaserBeam:
      return WeaponType.EnergyLaserBeam;
    case WeaponNamedType.ExplosiveMissile:
      return WeaponType.ExplosiveMissile;
    case WeaponNamedType.AirScoutMachineGun:
      return WeaponType.AirScoutMachineGun;
  }
}

/**
 * Game update delta containing all game state changes in a single tick.
 * Consolidates moves, attacks, spawns, actions, and other updates.
 */
export interface GameUpdateDelta {
  moves?: Array<{
    playerId: number;
    id: number;
    x: number;
    y: number;
    tx: number;
    ty: number;
  }>;
  attacks?: Array<{
    playerId: number;
    id: number;
    damage: number;
    targetPlayerId: number;
    targetId: number;
  }>;
  spawnUnits?: Array<{
    playerId: number;
    id: number;
    k: UnitType;
    x: number;
    y: number;
    tx?: number;
    ty?: number;
    life?: number;
    w?: WeaponType;
    s?: UnitStance;
    gx?: number;
    gy?: number;
  }>;
  spawnBuildings?: Array<{
    playerId: number;
    id: number;
    k: BuildingType;
    x: number;
    y: number;
    life?: number;
  }>;
  actions?: Array<{
    playerId: number;
    id?: number;
    k: string;
    targetId?: number;
    buildingId?: number;
    oreId?: number;
    status?: string;
  }>;
  setUnitStance?: Array<{
    playerId: number;
    id: number;
    stance: UnitStance;
  }>;
}
